import type {
  ApiResponseNoticeList,
  ApiResponseNotice,
  ApiResponseFaqList,
  ApiResponseFaq,
  ApiResponsePopupList,
  ApiResponsePopup,
  ApiResponseFontList,
  ApiResponseFont,
  ApiResponsePortfolioList,
  ApiResponsePortfolio,
  ApiResponsePortfolioSection,
  ApiResponsePortfolioButtonList,
  ApiResponsePortfolioLinkedOrderList,
} from "../../../shared/types/api";
import type {
  NoticeCreateRequest,
  NoticeUpdateRequest,
  FaqCreateRequest,
  FaqUpdateRequest,
  FaqOrderUpdateRequest,
  PopupReorderRequest,
  FontCreateRequest,
  FontUpdateRequest,
  PortfolioCreateRequest,
  PortfolioUpdateRequest,
  PortfolioReorderRequest,
  PortfolioButtonsUpdateRequest,
  PortfolioOrderLinksUpdateRequest,
} from "../../../domain/types/content";
import { httpClient } from "../http-client";
import { NEW_API_ENDPOINTS } from "../../../shared/constants/api";

export class ContentApiService {
  // ========== 공지사항 ==========

  async getNotices(companyId: number): Promise<ApiResponseNoticeList> {
    const response = await httpClient.get<ApiResponseNoticeList>(
      NEW_API_ENDPOINTS.contentController.notices.GET.list(companyId)
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "공지사항 목록을 가져오지 못했습니다."
      );
    }

    return response.data;
  }

  async getNotice(companyId: number, noticeId: number): Promise<ApiResponseNotice> {
    const response = await httpClient.get<ApiResponseNotice>(
      NEW_API_ENDPOINTS.contentController.notices.GET.detail(companyId, noticeId)
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "공지사항을 가져오지 못했습니다."
      );
    }

    return response.data;
  }

  async createNotice(
    companyId: number,
    request: NoticeCreateRequest
  ): Promise<ApiResponseNotice> {
    const response = await httpClient.post<ApiResponseNotice>(
      NEW_API_ENDPOINTS.contentController.notices.POST(companyId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "공지사항 생성에 실패했습니다."
      );
    }

    return response.data;
  }

  async updateNotice(
    companyId: number,
    noticeId: number,
    request: NoticeUpdateRequest
  ): Promise<ApiResponseNotice> {
    const response = await httpClient.put<ApiResponseNotice>(
      NEW_API_ENDPOINTS.contentController.notices.PUT(companyId, noticeId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "공지사항 수정에 실패했습니다."
      );
    }

    return response.data;
  }

  async deleteNotice(companyId: number, noticeId: number): Promise<void> {
    const response = await httpClient.delete(
      NEW_API_ENDPOINTS.contentController.notices.DELETE(companyId, noticeId)
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "공지사항 삭제에 실패했습니다."
      );
    }
  }

  // ========== FAQ ==========

  async getFaqs(companyId: number, category?: string): Promise<ApiResponseFaqList> {
    const response = await httpClient.get<ApiResponseFaqList>(
      NEW_API_ENDPOINTS.contentController.faqs.GET.list(companyId, category)
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "FAQ 목록을 가져오지 못했습니다."
      );
    }

    return response.data;
  }

  async getFaq(companyId: number, faqId: number): Promise<ApiResponseFaq> {
    const response = await httpClient.get<ApiResponseFaq>(
      NEW_API_ENDPOINTS.contentController.faqs.GET.detail(companyId, faqId)
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "FAQ를 가져오지 못했습니다."
      );
    }

    return response.data;
  }

  async createFaq(
    companyId: number,
    request: FaqCreateRequest
  ): Promise<ApiResponseFaq> {
    const response = await httpClient.post<ApiResponseFaq>(
      NEW_API_ENDPOINTS.contentController.faqs.POST(companyId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "FAQ 생성에 실패했습니다."
      );
    }

    return response.data;
  }

  async updateFaq(
    companyId: number,
    faqId: number,
    request: FaqUpdateRequest
  ): Promise<ApiResponseFaq> {
    const response = await httpClient.put<ApiResponseFaq>(
      NEW_API_ENDPOINTS.contentController.faqs.PUT(companyId, faqId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "FAQ 수정에 실패했습니다."
      );
    }

    return response.data;
  }

  async updateFaqOrder(
    companyId: number,
    faqId: number,
    request: FaqOrderUpdateRequest
  ): Promise<void> {
    const response = await httpClient.patch(
      NEW_API_ENDPOINTS.contentController.faqs.PATCH(companyId, faqId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "FAQ 순서 변경에 실패했습니다."
      );
    }
  }

  async deleteFaq(companyId: number, faqId: number): Promise<void> {
    const response = await httpClient.delete(
      NEW_API_ENDPOINTS.contentController.faqs.DELETE(companyId, faqId)
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "FAQ 삭제에 실패했습니다."
      );
    }
  }

  // ========== 팝업 ==========

  async getPopups(companyId: number): Promise<ApiResponsePopupList> {
    const response = await httpClient.get<ApiResponsePopupList>(
      NEW_API_ENDPOINTS.contentController.popups.GET.list(companyId)
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "팝업 목록을 가져오지 못했습니다."
      );
    }

    return response.data;
  }

  async createPopup(
    companyId: number,
    name: string,
    isEnabled: boolean,
    popupImage?: File
  ): Promise<ApiResponsePopup> {
    const formData = new FormData();
    formData.append("name", name);
    formData.append("isEnabled", String(isEnabled));
    if (popupImage) {
      formData.append("popupImage", popupImage);
    }

    const response = await httpClient.post<ApiResponsePopup>(
      NEW_API_ENDPOINTS.contentController.popups.POST(companyId),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "팝업 생성에 실패했습니다."
      );
    }

    return response.data;
  }

  async updatePopup(
    companyId: number,
    popupId: number,
    name: string,
    isEnabled: boolean,
    popupImage?: File
  ): Promise<ApiResponsePopup> {
    const formData = new FormData();
    formData.append("name", name);
    formData.append("isEnabled", String(isEnabled));
    if (popupImage) {
      formData.append("popupImage", popupImage);
    }

    const response = await httpClient.put<ApiResponsePopup>(
      NEW_API_ENDPOINTS.contentController.popups.PUT.update(companyId, popupId),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "팝업 수정에 실패했습니다."
      );
    }

    return response.data;
  }

  async reorderPopups(
    companyId: number,
    request: PopupReorderRequest
  ): Promise<void> {
    const response = await httpClient.put(
      NEW_API_ENDPOINTS.contentController.popups.PUT.reorder(companyId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "팝업 순서 변경에 실패했습니다."
      );
    }
  }

  async deletePopup(companyId: number, popupId: number): Promise<void> {
    const response = await httpClient.delete(
      NEW_API_ENDPOINTS.contentController.popups.DELETE(companyId, popupId)
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "팝업 삭제에 실패했습니다."
      );
    }
  }

  // ========== 폰트 ==========

  async getFonts(companyId: number): Promise<ApiResponseFontList> {
    const response = await httpClient.get<ApiResponseFontList>(
      NEW_API_ENDPOINTS.contentController.fonts.GET.list(companyId)
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "폰트 목록을 가져오지 못했습니다."
      );
    }

    return response.data;
  }

  async createFont(
    companyId: number,
    request: FontCreateRequest
  ): Promise<ApiResponseFont> {
    const response = await httpClient.post<ApiResponseFont>(
      NEW_API_ENDPOINTS.contentController.fonts.POST(companyId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "폰트 생성에 실패했습니다."
      );
    }

    return response.data;
  }

  async updateFont(
    companyId: number,
    fontId: number,
    request: FontUpdateRequest
  ): Promise<ApiResponseFont> {
    const response = await httpClient.put<ApiResponseFont>(
      NEW_API_ENDPOINTS.contentController.fonts.PUT(companyId, fontId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "폰트 수정에 실패했습니다."
      );
    }

    return response.data;
  }

  async deleteFont(companyId: number, fontId: number): Promise<void> {
    const response = await httpClient.delete(
      NEW_API_ENDPOINTS.contentController.fonts.DELETE(companyId, fontId)
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "폰트 삭제에 실패했습니다."
      );
    }
  }

  // ========== 포트폴리오 ==========

  /**
   * 생성/수정 본문을 multipart 로 만든다.
   * 대표 이미지(thumbnail)와 추가 이미지(images)는 백엔드가 @RequestPart 로 받고,
   * 나머지 텍스트 필드는 @ModelAttribute 로 바인딩된다.
   */
  private buildPortfolioFormData(
    request: PortfolioCreateRequest | PortfolioUpdateRequest,
    thumbnail?: File,
    images?: File[]
  ): FormData {
    const formData = new FormData();
    formData.append("title", request.title);
    formData.append("summary", request.summary ?? "");
    formData.append("description", request.description ?? "");
    formData.append("clientName", request.clientName ?? "");
    formData.append("workedAt", request.workedAt ?? "");
    formData.append("isPublished", String(request.isPublished));

    if (thumbnail) {
      formData.append("thumbnail", thumbnail);
    }
    // images 를 아예 보내지 않으면 서버가 기존 이미지를 유지한다.
    if (images) {
      images.forEach((image) => formData.append("images", image));
    }

    return formData;
  }

  async getPortfolios(companyId: number): Promise<ApiResponsePortfolioList> {
    const response = await httpClient.get<ApiResponsePortfolioList>(
      NEW_API_ENDPOINTS.contentController.portfolios.GET.list(companyId)
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "포트폴리오 목록을 가져오지 못했습니다."
      );
    }

    return response.data;
  }

  async getPortfolio(
    companyId: number,
    portfolioId: number
  ): Promise<ApiResponsePortfolio> {
    const response = await httpClient.get<ApiResponsePortfolio>(
      NEW_API_ENDPOINTS.contentController.portfolios.GET.detail(companyId, portfolioId)
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "포트폴리오를 가져오지 못했습니다."
      );
    }

    return response.data;
  }

  async createPortfolio(
    companyId: number,
    request: PortfolioCreateRequest,
    thumbnail?: File,
    images?: File[]
  ): Promise<ApiResponsePortfolio> {
    const response = await httpClient.post<ApiResponsePortfolio>(
      NEW_API_ENDPOINTS.contentController.portfolios.POST(companyId),
      this.buildPortfolioFormData(request, thumbnail, images),
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "포트폴리오 생성에 실패했습니다."
      );
    }

    return response.data;
  }

  async updatePortfolio(
    companyId: number,
    portfolioId: number,
    request: PortfolioUpdateRequest,
    thumbnail?: File,
    images?: File[]
  ): Promise<ApiResponsePortfolio> {
    const response = await httpClient.put<ApiResponsePortfolio>(
      NEW_API_ENDPOINTS.contentController.portfolios.PUT.update(companyId, portfolioId),
      this.buildPortfolioFormData(request, thumbnail, images),
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "포트폴리오 수정에 실패했습니다."
      );
    }

    return response.data;
  }

  async reorderPortfolios(
    companyId: number,
    request: PortfolioReorderRequest
  ): Promise<void> {
    const response = await httpClient.put(
      NEW_API_ENDPOINTS.contentController.portfolios.PUT.reorder(companyId),
      request
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "포트폴리오 순서 변경에 실패했습니다."
      );
    }
  }

  async deletePortfolio(companyId: number, portfolioId: number): Promise<void> {
    const response = await httpClient.delete(
      NEW_API_ENDPOINTS.contentController.portfolios.DELETE(companyId, portfolioId)
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "포트폴리오 삭제에 실패했습니다."
      );
    }
  }

  async getPortfolioSection(
    companyId: number
  ): Promise<ApiResponsePortfolioSection> {
    const response = await httpClient.get<ApiResponsePortfolioSection>(
      NEW_API_ENDPOINTS.contentController.portfolios.GET.section(companyId)
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "포트폴리오 섹션 설정을 가져오지 못했습니다."
      );
    }

    return response.data;
  }

  async updatePortfolioSection(
    companyId: number,
    isEnabled: boolean
  ): Promise<ApiResponsePortfolioSection> {
    const response = await httpClient.put<ApiResponsePortfolioSection>(
      NEW_API_ENDPOINTS.contentController.portfolios.PUT.section(companyId),
      { isEnabled }
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "포트폴리오 섹션 설정 변경에 실패했습니다."
      );
    }

    return response.data;
  }

  /** 버튼 목록 전체 치환 */
  async updatePortfolioButtons(
    companyId: number,
    portfolioId: number,
    request: PortfolioButtonsUpdateRequest
  ): Promise<ApiResponsePortfolioButtonList> {
    const response = await httpClient.put<ApiResponsePortfolioButtonList>(
      NEW_API_ENDPOINTS.contentController.portfolios.PUT.buttons(
        companyId,
        portfolioId
      ),
      request
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "포트폴리오 버튼 저장에 실패했습니다."
      );
    }

    return response.data;
  }

  /** 연결 주문 전체 치환 */
  async updatePortfolioOrderLinks(
    companyId: number,
    portfolioId: number,
    request: PortfolioOrderLinksUpdateRequest
  ): Promise<ApiResponsePortfolioLinkedOrderList> {
    const response = await httpClient.put<ApiResponsePortfolioLinkedOrderList>(
      NEW_API_ENDPOINTS.contentController.portfolios.PUT.orders(
        companyId,
        portfolioId
      ),
      request
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "포트폴리오 주문 연결 저장에 실패했습니다."
      );
    }

    return response.data;
  }
}

export const contentApiService = new ContentApiService();
